// v3.18-1337 LAC-001 — useLiveActivityClaim hook.
//
// A workflow calls `useLiveActivityClaim({ clientId, workflow })` on
// mount.  The hook:
//   * POSTs `/live-activity-claims/start`;
//   * pings `/heartbeat` every 20 s while the workflow stays open;
//   * POSTs `/release` on unmount + on `beforeunload`;
//   * surfaces `collision` details so the workspace can render the
//     mandatory "someone else is already doing this" modal.
//
// Return shape:
//   {
//     status: 'idle' | 'claimed' | 'collision' | 'error',
//     claim:      the active claim (present when status === 'claimed'),
//     collision:  { claim: {...}, activity_label: '...' },
//     retry:      () => attempt to claim again (post-Continue),
//     release:    () => voluntary release,
//   }
//
// The hook never blocks the render — collision handling is up to the
// consumer via the returned `collision` payload.
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../lib/api';

const HEARTBEAT_MS = 20_000;

export function useLiveActivityClaim({ clientId, workflow, metadata, enabled = true, autoRetake = false }) {
  const [status, setStatus]       = useState('idle');
  const [claim, setClaim]         = useState(null);
  const [collision, setCollision] = useState(null);
  const heartbeatTimerRef = useRef(null);
  const claimIdRef        = useRef(null);
  const activeRef         = useRef(true);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback((claimId) => {
    stopHeartbeat();
    heartbeatTimerRef.current = setInterval(async () => {
      try {
        await api.post(`/live-activity-claims/${claimId}/heartbeat`);
      } catch { /* silent — sweep will reclaim if needed */ }
    }, HEARTBEAT_MS);
  }, [stopHeartbeat]);

  const attempt = useCallback(async () => {
    if (!clientId || !workflow) return;
    try {
      const { data } = await api.post('/live-activity-claims/start', {
        client_id: clientId, workflow, metadata: metadata || undefined,
      });
      if (!activeRef.current) return;
      if (data.ok) {
        setStatus('claimed');
        setClaim(data.claim);
        setCollision(null);
        claimIdRef.current = data.claim.id;
        startHeartbeat(data.claim.id);
      } else if (data.status === 'collision') {
        setStatus('collision');
        setClaim(null);
        setCollision({ claim: data.claim, activity_label: data.activity_label });
      }
    } catch (e) {
      if (!activeRef.current) return;
      setStatus('error');
      setCollision(null);
    }
  }, [clientId, workflow, metadata, startHeartbeat]);

  const release = useCallback(async () => {
    const id = claimIdRef.current;
    stopHeartbeat();
    claimIdRef.current = null;
    if (!id) return;
    try { await api.post(`/live-activity-claims/${id}/release`); }
    catch { /* silent */ }
  }, [stopHeartbeat]);

  useEffect(() => {
    activeRef.current = true;
    if (enabled) attempt();
    const onBeforeUnload = () => {
      const id = claimIdRef.current;
      if (!id) return;
      // beacon-style release so browser close still frees the claim
      const base = (api.defaults?.baseURL || '').replace(/\/+$/, '');
      try {
        navigator.sendBeacon?.(`${base}/live-activity-claims/${id}/release`);
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      activeRef.current = false;
      window.removeEventListener('beforeunload', onBeforeUnload);
      release();
    };
  }, [clientId, workflow, enabled]);

  const retry = useCallback(() => attempt(), [attempt]);

  // Optional: after a takeover from a director surface, allow the
  // consumer to retake ownership without unmounting.
  useEffect(() => {
    if (autoRetake && status === 'collision') attempt();
  }, [autoRetake, status, attempt]);

  return { status, claim, collision, retry, release };
}

export default useLiveActivityClaim;
